// Manager Coaching Records — Priority 7.
// A durable behaviour-change record layer for the manager:
//   1. Pick an agent → AI suggests the highest-leverage coaching focus.
//   2. Review 3 evidence calls (deep-linked to the call-detail page).
//   3. Create a coaching record with focus + approved language + words to avoid.
//   4. Track ACTIVE records for the team, including baseline vs after progress.
//   5. Mark IMPROVED / PARTIAL / NOT_IMPROVED / CONTINUE / COMPLETED.
//
// Backend endpoints in /app/backend/routes/coaching.py:
//   GET  /coaching/records
//   GET  /coaching/suggest/{consultant_id}
//   POST /coaching/records
//   PATCH /coaching/records/{id}/status
//   GET  /coaching/records/{id}/progress
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Users, Sparkles, PlusCircle, TrendingUp, TrendingDown,
  CheckCircle2, XCircle, Clock, ArrowRight, RotateCcw, Award, ShieldAlert,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  active:       { label: 'Active',       tone: 'bg-indigo-100 text-indigo-800' },
  improved:     { label: 'Improved',     tone: 'bg-emerald-100 text-emerald-800' },
  partial:      { label: 'Partial',      tone: 'bg-amber-100 text-amber-800' },
  not_improved: { label: 'Not Improved', tone: 'bg-rose-100 text-rose-800' },
  continue:     { label: 'Continue',     tone: 'bg-sky-100 text-sky-800' },
  completed:    { label: 'Completed',    tone: 'bg-slate-200 text-slate-700' },
};

const ProgressBadge = ({ delta }) => {
  if (delta == null) return <span className="text-xs text-slate-400">baseline only</span>;
  const positive = delta >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  const tone = positive ? 'text-emerald-700 bg-emerald-50' : 'text-rose-700 bg-rose-50';
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${tone}`}>
      <Icon className="w-3 h-3" />
      {positive ? '+' : ''}{delta}pts vs baseline
    </span>
  );
};

const ManagerCoachingRecordsPage = () => {
  const { token, user } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [agents, setAgents] = useState([]);
  const [records, setRecords] = useState([]);
  const [progressById, setProgressById] = useState({});
  const [streaks, setStreaks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [suggestingFor, setSuggestingFor] = useState(null);
  const [suggestion, setSuggestion] = useState(null);
  const [creatingFor, setCreatingFor] = useState(null); // agent_id being coached
  const [savingRecord, setSavingRecord] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  const isManager = user && ['master_admin', 'admin', 'manager', 'supervisor', 'domain_admin', 'domain_supervisor', 'office_manager'].includes(user.role);

  // ── Load team + records ──────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [agentsR, recordsR, streaksR] = await Promise.all([
        axios.get(`${API}/analytics/my-coaching/agents`, { headers }).catch(() => ({ data: { agents: [] } })),
        axios.get(`${API}/coaching/records`, { headers }),
        axios.get(`${API}/coaching/governance-streaks?days=30&min_breaches=3`, { headers }).catch(() => ({ data: { streaks: [] } })),
      ]);
      setAgents(agentsR.data?.agents || []);
      setStreaks(streaksR.data?.streaks || []);
      const recs = recordsR.data?.records || [];
      setRecords(recs);

      // Fetch progress for every active record in parallel.
      const progressResults = await Promise.all(
        recs
          .filter(r => r.status === 'active' || r.status === 'continue')
          .map(r =>
            axios.get(`${API}/coaching/records/${r.id}/progress`, { headers })
              .then(res => [r.id, res.data])
              .catch(() => [r.id, null]),
          ),
      );
      setProgressById(Object.fromEntries(progressResults));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load coaching records');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    if (!token || !isManager) return;
    load();
  }, [token, isManager, load]);

  // ── Suggest a focus for an agent ─────────────────────────
  const suggestFocus = async (agent) => {
    setSuggestingFor(agent.agent_id);
    setSuggestion(null);
    setCreatingFor(null);
    try {
      const r = await axios.get(`${API}/coaching/suggest/${agent.agent_id}`, { headers });
      if (!r.data?.suggestion) {
        toast.info(r.data?.reason || 'Not enough graded calls to identify a focus yet');
        setSuggestingFor(null);
        return;
      }
      setSuggestion({ ...r.data.suggestion, agent });
      setCreatingFor(agent.agent_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Suggestion failed');
    } finally {
      setSuggestingFor(null);
    }
  };

  // ── Save a new coaching record ───────────────────────────
  const saveRecord = async () => {
    if (!suggestion) return;
    setSavingRecord(true);
    try {
      await axios.post(`${API}/coaching/records`, {
        consultantId: suggestion.agent.agent_id,
        focusCriterion: suggestion.focusCriterion,
        focusBehaviour: suggestion.focusBehaviour,
        baselineScore: suggestion.baselineScore,
        evidenceCalls: suggestion.evidenceCalls,
        approvedLanguage: suggestion.approvedLanguage,
        wordsToAvoid: suggestion.wordsToAvoid,
      }, { headers });
      toast.success(`Coaching record created for ${suggestion.agent.agent_name || suggestion.agent.name}`);
      setSuggestion(null);
      setCreatingFor(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create record');
    } finally {
      setSavingRecord(false);
    }
  };

  // ── Update a record's status ─────────────────────────────
  const updateStatus = async (recordId, status) => {
    try {
      await axios.patch(`${API}/coaching/records/${recordId}/status`, { status }, { headers });
      toast.success(`Marked ${STATUS_LABELS[status].label}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update');
    }
  };

  if (!isManager) {
    return (
      <div className="p-6 text-center text-slate-500">
        Managers only. See your active coaching on the <Link to="/my/performance" className="text-teal-600 underline">My Performance</Link> page.
      </div>
    );
  }

  const filteredRecords = statusFilter === 'all'
    ? records
    : records.filter(r => r.status === statusFilter);
  const agentName = (aid) => {
    const a = agents.find(x => x.agent_id === aid);
    return a?.agent_name || a?.name || aid;
  };
  const activeCount = records.filter(r => r.status === 'active' || r.status === 'continue').length;

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="manager-coaching-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Award className="w-6 h-6 text-indigo-600" /> Coaching Records
        </h1>
        <p className="text-slate-500 text-sm mt-0.5">
          Durable behaviour-change records for your team — each one has a focus criterion, evidence calls, approved language, and before/after progress tracked automatically.
        </p>
      </div>

      {/* Governance streak — repeat compliance offenders (last 30 days) */}
      {streaks.length > 0 && (
        <section
          className="bg-red-50 border border-red-200 rounded-xl p-5"
          data-testid="governance-streaks-section"
        >
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-red-900 font-semibold flex items-center gap-2">
                <ShieldAlert className="w-4 h-4 text-red-700" />
                Governance streak · last 30 days
              </h2>
              <p className="text-red-700/70 text-xs mt-0.5">
                Agents with 3+ compliance-flagged calls. Coach them before it becomes a pattern.
              </p>
            </div>
            <span className="text-[11px] font-semibold px-2 py-1 rounded-full bg-red-200 text-red-900">
              {streaks.length} agent{streaks.length === 1 ? '' : 's'}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {streaks.map(s => {
              const agentObj = agents.find(a => a.agent_id === s.agent_id) || {
                agent_id: s.agent_id, agent_name: s.agent_name,
              };
              const last = s.latest_violation;
              const daysAgo = s.last_breach_at
                ? Math.max(0, Math.floor((Date.now() - new Date(s.last_breach_at).getTime()) / (24 * 3600 * 1000)))
                : null;
              return (
                <div
                  key={`${s.domain_id || ''}:${s.agent_id}`}
                  className="bg-white border border-red-200 rounded-lg p-3 flex flex-col gap-2"
                  data-testid={`governance-streak-${s.agent_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{s.agent_name}</p>
                      <p className="text-[11px] text-slate-500">
                        {s.breach_count} breaches
                        {daysAgo != null && ` · last ${daysAgo === 0 ? 'today' : daysAgo + 'd ago'}`}
                      </p>
                    </div>
                    <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-600 text-white whitespace-nowrap">
                      {s.breach_count}× breach
                    </span>
                  </div>
                  {last?.violations?.length > 0 && (
                    <blockquote
                      className="text-[11px] text-red-800 italic border-l-2 border-red-300 pl-2 line-clamp-2"
                      title={last.violations.join('\n')}
                    >
                      &ldquo;{last.violations[0]}&rdquo;
                    </blockquote>
                  )}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => suggestFocus(agentObj)}
                      disabled={suggestingFor === s.agent_id}
                      className="flex-1 flex items-center justify-center gap-1 text-xs bg-red-100 hover:bg-red-200 text-red-800 border border-red-300 rounded-md px-2 py-1 disabled:opacity-50"
                      data-testid={`governance-coach-${s.agent_id}`}
                    >
                      {suggestingFor === s.agent_id
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <><Sparkles className="w-3 h-3" />Start coaching</>}
                    </button>
                    {last?.call_id && (
                      <Link
                        to={`/calls/${last.call_id}`}
                        className="flex items-center gap-1 text-xs text-red-700 hover:text-red-900 underline"
                      >
                        View call <ArrowRight className="w-3 h-3" />
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Team → start a coaching record */}
      <section className="bg-white border border-slate-200 rounded-xl p-5">
        <h2 className="text-slate-900 font-semibold flex items-center gap-2 mb-4">
          <Users className="w-4 h-4 text-slate-500" /> Team
        </h2>
        {agents.length === 0 ? (
          <p className="text-slate-500 text-sm italic">No agents in scope. Check site allocation on the User settings tab.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {agents.map(a => (
              <div
                key={a.agent_id}
                className="border border-slate-200 rounded-lg p-3 flex items-center justify-between"
                data-testid={`coaching-agent-${a.agent_id}`}
              >
                <div>
                  <p className="text-sm font-semibold text-slate-900">{a.agent_name || a.name}</p>
                  <p className="text-[11px] text-slate-500">ext {a.extension || a.agent_id} · {a.call_count || 0} calls</p>
                </div>
                <button
                  onClick={() => suggestFocus(a)}
                  disabled={suggestingFor === a.agent_id}
                  className="flex items-center gap-1 text-xs bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 rounded-lg px-2.5 py-1.5 disabled:opacity-50"
                  data-testid={`coaching-suggest-${a.agent_id}`}
                >
                  {suggestingFor === a.agent_id
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <><Sparkles className="w-3.5 h-3.5" />Suggest focus</>}
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Suggestion → confirm & save */}
      {suggestion && creatingFor && (
        <section
          className="bg-indigo-50 border border-indigo-200 rounded-xl p-5"
          data-testid="coaching-suggestion-card"
        >
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="text-xs uppercase tracking-wider text-indigo-700 font-semibold">AI suggested focus for</p>
              <p className="text-slate-900 text-lg font-semibold">{suggestion.agent.agent_name || suggestion.agent.name}</p>
            </div>
            <button
              onClick={() => { setSuggestion(null); setCreatingFor(null); }}
              className="text-slate-500 hover:text-slate-700 text-xs"
            >
              Dismiss
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-[11px] uppercase text-slate-500 font-semibold mb-1">Focus criterion</p>
              <p className="text-sm text-slate-900 font-medium">{suggestion.focusCriterion.replace(/_/g, ' ')}</p>
              <p className="text-xs text-slate-600 mt-1">Baseline score: <span className="font-semibold">{suggestion.baselineScore ?? '—'}</span></p>
              {suggestion.whyItMatters && (
                <p className="text-xs text-slate-600 italic mt-2">{suggestion.whyItMatters}</p>
              )}
            </div>

            <div>
              <p className="text-[11px] uppercase text-slate-500 font-semibold mb-1">Approved language ({suggestion.approvedLanguage?.length || 0})</p>
              <ul className="text-xs text-emerald-900 space-y-1">
                {(suggestion.approvedLanguage || []).slice(0, 3).map((s) => (
                  <li key={s} className="bg-emerald-50 border border-emerald-200 rounded px-2 py-1 italic">&ldquo;{s}&rdquo;</li>
                ))}
              </ul>
              {suggestion.wordsToAvoid?.length > 0 && (
                <div className="mt-2">
                  <p className="text-[11px] uppercase text-slate-500 font-semibold mb-1">Words to avoid</p>
                  <div className="flex flex-wrap gap-1">
                    {suggestion.wordsToAvoid.map((w) => (
                      <span key={w} className="text-[11px] text-rose-800 bg-rose-100 border border-rose-200 rounded-full px-2 py-0.5">{w}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {suggestion.evidenceCalls?.length > 0 && (
            <div className="mt-4 border-t border-indigo-200 pt-3">
              <p className="text-[11px] uppercase text-slate-500 font-semibold mb-2">Evidence — review before assigning</p>
              <div className="flex flex-wrap gap-2">
                {suggestion.evidenceCalls.map((cid) => (
                  <Link
                    key={cid}
                    to={`/calls/${cid}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-indigo-700 bg-white border border-indigo-300 rounded-full px-3 py-1 hover:bg-indigo-100"
                    data-testid={`coaching-evidence-${cid}`}
                  >
                    Call {cid.slice(0, 8)} <ArrowRight className="w-3 h-3" />
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4 flex justify-end">
            <button
              onClick={saveRecord}
              disabled={savingRecord}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg px-4 py-2 disabled:opacity-50"
              data-testid="coaching-create-record-btn"
            >
              {savingRecord ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlusCircle className="w-4 h-4" />}
              Create coaching record
            </button>
          </div>
        </section>
      )}

      {/* Active records */}
      <section className="bg-white border border-slate-200 rounded-xl">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="text-slate-900 font-semibold">Records</h2>
            <p className="text-xs text-slate-500 mt-0.5">{activeCount} active · {records.length} total</p>
          </div>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="text-xs border border-slate-300 rounded-lg px-2 py-1"
            data-testid="coaching-status-filter"
          >
            <option value="all">All</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-slate-300 animate-spin" /></div>
        ) : filteredRecords.length === 0 ? (
          <p className="text-center text-slate-500 text-sm py-8 italic">
            No coaching records {statusFilter !== 'all' ? `with status "${STATUS_LABELS[statusFilter].label}"` : 'yet'}.
            Pick an agent above to start.
          </p>
        ) : (
          <div className="divide-y divide-slate-200">
            {filteredRecords.map(r => {
              const prog = progressById[r.id];
              const statusMeta = STATUS_LABELS[r.status] || STATUS_LABELS.active;
              return (
                <div key={r.id} className="p-5" data-testid={`coaching-record-${r.id}`}>
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-slate-900">{agentName(r.consultantId)}</span>
                        <span className="text-xs text-slate-400">·</span>
                        <span className="text-sm text-slate-700">{r.focusCriterion?.replace(/_/g, ' ')}</span>
                        <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${statusMeta.tone}`}>
                          {statusMeta.label}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Assigned {new Date(r.assignedDate).toLocaleDateString()} by {r.managerName} · reviewing after {r.reassessmentCallCount} calls
                      </p>
                    </div>

                    {/* Progress vs baseline */}
                    <div className="text-right shrink-0">
                      <div className="flex items-center gap-3">
                        <div>
                          <p className="text-[10px] uppercase text-slate-400 font-semibold">Baseline</p>
                          <p className="text-base font-bold text-slate-800">{r.baselineScore ?? '—'}</p>
                        </div>
                        <ArrowRight className="w-4 h-4 text-slate-300" />
                        <div>
                          <p className="text-[10px] uppercase text-slate-400 font-semibold">After</p>
                          <p className="text-base font-bold text-slate-800">{prog?.after_avg ?? '—'}</p>
                        </div>
                      </div>
                      <div className="mt-1">
                        <ProgressBadge delta={prog?.delta} />
                      </div>
                    </div>
                  </div>

                  {/* Approved language + evidence */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                    {r.approvedLanguage?.length > 0 && (
                      <div className="text-xs bg-emerald-50 border border-emerald-200 rounded p-2">
                        <p className="text-[10px] uppercase text-emerald-800 font-semibold mb-1">Approved language</p>
                        <ul className="space-y-0.5 text-emerald-900 italic">
                          {r.approvedLanguage.slice(0, 2).map((s) => <li key={s}>&ldquo;{s}&rdquo;</li>)}
                        </ul>
                      </div>
                    )}
                    {r.evidenceCalls?.length > 0 && (
                      <div className="text-xs">
                        <p className="text-[10px] uppercase text-slate-500 font-semibold mb-1">Evidence calls</p>
                        <div className="flex flex-wrap gap-1">
                          {r.evidenceCalls.map((cid) => (
                            <Link
                              key={cid}
                              to={`/calls/${cid}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-[11px] text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-2 py-0.5 hover:bg-indigo-100"
                            >
                              {cid.slice(0, 8)} <ArrowRight className="w-3 h-3" />
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Status actions (only for active/continue records) */}
                  {(r.status === 'active' || r.status === 'continue') && (
                    <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap gap-2">
                      <button
                        onClick={() => updateStatus(r.id, 'improved')}
                        className="flex items-center gap-1 text-xs bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-200 rounded px-2.5 py-1"
                        data-testid={`coaching-mark-improved-${r.id}`}
                      >
                        <CheckCircle2 className="w-3 h-3" /> Improved
                      </button>
                      <button
                        onClick={() => updateStatus(r.id, 'partial')}
                        className="flex items-center gap-1 text-xs bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 rounded px-2.5 py-1"
                        data-testid={`coaching-mark-partial-${r.id}`}
                      >
                        <Clock className="w-3 h-3" /> Partial
                      </button>
                      <button
                        onClick={() => updateStatus(r.id, 'not_improved')}
                        className="flex items-center gap-1 text-xs bg-rose-50 hover:bg-rose-100 text-rose-800 border border-rose-200 rounded px-2.5 py-1"
                        data-testid={`coaching-mark-not-improved-${r.id}`}
                      >
                        <XCircle className="w-3 h-3" /> Not improved
                      </button>
                      {r.status !== 'continue' && (
                        <button
                          onClick={() => updateStatus(r.id, 'continue')}
                          className="flex items-center gap-1 text-xs bg-sky-50 hover:bg-sky-100 text-sky-800 border border-sky-200 rounded px-2.5 py-1"
                          data-testid={`coaching-mark-continue-${r.id}`}
                        >
                          <RotateCcw className="w-3 h-3" /> Continue
                        </button>
                      )}
                      <button
                        onClick={() => updateStatus(r.id, 'completed')}
                        className="flex items-center gap-1 text-xs bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-300 rounded px-2.5 py-1 ml-auto"
                        data-testid={`coaching-mark-completed-${r.id}`}
                      >
                        Complete
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default ManagerCoachingRecordsPage;
